import React from 'react';
import { createPortal } from 'react-dom';
import { Theme } from '../Theme';

export type TableRowEllipsisMenuOption = {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    destructive?: boolean;
};

type TableRowEllipsisMenuButtonProps = {
    options: Array<TableRowEllipsisMenuOption>;
    ariaLabel?: string;
    isVisible?: boolean;
};

type MenuPosition = {
    top: number;
    right: number;
};

const MENU_OFFSET_PX = 6;

export function TableRowEllipsisMenuButton(props: TableRowEllipsisMenuButtonProps) {
    const { options, ariaLabel = 'Row options', isVisible = true } = props;
    const [isOpen, setIsOpen] = React.useState(false);
    const [hoveredOptionIndex, setHoveredOptionIndex] = React.useState<number | null>(null);
    const [menuPosition, setMenuPosition] = React.useState<MenuPosition>({ top: 0, right: 0 });
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);
    const menuRef = React.useRef<HTMLDivElement | null>(null);
    const shouldShowButton = isVisible || isOpen;

    const updateMenuPosition = React.useCallback(() => {
        // Position the popup against the viewport so table overflow cannot crop it.
        const buttonRect = buttonRef.current?.getBoundingClientRect();
        if (!buttonRect) {
            return;
        }

        setMenuPosition({
            top: buttonRect.bottom + MENU_OFFSET_PX,
            right: Math.max(8, window.innerWidth - buttonRect.right),
        });
    }, []);

    const closeMenu = React.useCallback(() => {
        setIsOpen(false);
        setHoveredOptionIndex(null);
    }, []);

    React.useEffect(() => {
        // Close the row menu when the user clicks elsewhere on the page.
        if (!isOpen) {
            return;
        }

        const handleDocumentMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                !containerRef.current?.contains(target) &&
                !menuRef.current?.contains(target)
            ) {
                closeMenu();
            }
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);
        return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
    }, [closeMenu, isOpen]);

    React.useLayoutEffect(() => {
        // Keep the portal menu attached to the row button while scrolling/resizing.
        if (!isOpen) {
            return;
        }

        updateMenuPosition();
        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);

        return () => {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [isOpen, updateMenuPosition]);

    const menu = isOpen ? createPortal(
        <div
            ref={menuRef}
            style={{
                ...styles.menu,
                top: menuPosition.top,
                right: menuPosition.right,
            }}
            role='menu'
            onClick={(event) => event.stopPropagation()}
        >
            {options.map((option, optionIndex) => (
                <button
                    key={option.label}
                    type='button'
                    onMouseEnter={() => setHoveredOptionIndex(optionIndex)}
                    onMouseLeave={() => setHoveredOptionIndex(null)}
                    onFocus={() => setHoveredOptionIndex(optionIndex)}
                    onBlur={() => setHoveredOptionIndex(null)}
                    onClick={() => {
                        if (option.disabled) {
                            return;
                        }

                        closeMenu();
                        option.onClick();
                    }}
                    style={{
                        ...styles.menuItem,
                        ...(hoveredOptionIndex === optionIndex ? styles.menuItemHighlighted : {}),
                        ...(option.destructive ? styles.destructiveMenuItem : {}),
                        ...(option.disabled ? styles.disabledMenuItem : {}),
                    }}
                    disabled={option.disabled}
                    role='menuitem'
                >
                    {option.label}
                </button>
            ))}
        </div>,
        document.body
    ) : null;

    const handleButtonClick = () => {
        if (!isOpen) {
            updateMenuPosition();
            setIsOpen(true);
        } else {
            closeMenu();
        }
    };

    return (
        <div
            ref={containerRef}
            style={{
                ...styles.container,
                ...(shouldShowButton ? {} : styles.containerHidden),
            }}
            onClick={(event) => event.stopPropagation()}
        >
            <button
                ref={buttonRef}
                type='button'
                onClick={handleButtonClick}
                style={styles.button}
                aria-label={ariaLabel}
                aria-haspopup='menu'
                aria-expanded={isOpen}
            >
                <span style={styles.ellipsis}>…</span>
            </button>
            {menu}
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'opacity 120ms ease',
    },
    containerHidden: {
        opacity: 0,
        pointerEvents: 'none',
    },
    button: {
        ...Theme.Fonts.body,
        width: '34px',
        height: '34px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
    },
    ellipsis: {
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: 1,
        transform: 'translateY(-3px)',
    },
    menu: {
        position: 'fixed',
        zIndex: 2000,
        minWidth: '140px',
        padding: '6px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.background,
        boxShadow: '0 10px 28px rgba(0, 0, 0, 0.12)',
    },
    menuItem: {
        ...Theme.Fonts.body,
        width: '100%',
        boxSizing: 'border-box',
        appearance: 'none',
        WebkitAppearance: 'none',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
        cursor: 'pointer',
        padding: '8px 10px',
        textAlign: 'left',
        fontWeight: 600,
    },
    menuItemHighlighted: {
        backgroundColor: 'rgba(129, 126, 228, 0.14)',
    },
    destructiveMenuItem: {
        color: '#d32f2f',
    },
    disabledMenuItem: {
        cursor: 'not-allowed',
        opacity: 0.5,
    },
};
